import { GUEST_ROLES, type GuestRole } from "@mn/shared";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { Alert, Button, Field, SectionTitle } from "@/components/ui";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { PublishSection } from "./PublishSection";

interface Members {
  members: {
    id: string;
    name: string;
    email: string;
    role: GuestRole;
    since: string;
    lastViewedAt: string | null;
  }[];
  invites: {
    id: string;
    email: string | null;
    role: GuestRole;
    useCount: number;
    maxUses: number | null;
    expiresAt: string | null;
    revokedAt: string | null;
  }[];
}

export function ShareDialog({ boardId, onClose }: { boardId: string; onClose: () => void }) {
  const t = useT();
  const [data, setData] = useState<Members | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<GuestRole>("viewer");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(
    async () => setData(await api<Members>(`/api/boards/${boardId}/members`)),
    [boardId],
  );
  useEffect(() => {
    void load();
  }, [load]);

  async function invite(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const r = await api<{ url: string }>(`/api/boards/${boardId}/invites`, {
        method: "POST",
        json: { email: email || undefined, role },
      });
      if (!email) {
        await navigator.clipboard.writeText(r.url).catch(() => {});
        setMsg(t.linkCopied);
      } else setMsg(t.inviteSent);
      setEmail("");
      await load();
    } finally {
      setBusy(false);
    }
  }
  async function setMemberRole(userId: string, r: GuestRole) {
    await api(`/api/boards/${boardId}/members/${userId}`, { method: "PATCH", json: { role: r } });
    await load();
  }
  async function removeMember(userId: string) {
    await api(`/api/boards/${boardId}/members/${userId}`, { method: "DELETE" });
    await load();
  }
  async function revoke(inviteId: string) {
    await api(`/api/boards/${boardId}/invites/${inviteId}`, { method: "DELETE" });
    await load();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-black/70 p-4 pt-16"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-lg rounded-md border border-border bg-panel p-6 shadow-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal
      >
        <SectionTitle>{t.inviteGuest}</SectionTitle>
        <form onSubmit={invite} className="mb-6 flex flex-wrap items-end gap-2">
          <Field
            label={t.email}
            name="email"
            type="email"
            placeholder="cliente@empresa.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="min-w-52 flex-1"
          />
          <label className="block">
            <span className="mb-1.5 block text-sm font-semibold">{t.role}</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as GuestRole)}
              className="rounded-sm border border-border bg-panel-2 px-3 py-2.5"
            >
              {GUEST_ROLES.map((r) => (
                <option key={r} value={r}>
                  {t.roles[r]}
                </option>
              ))}
            </select>
          </label>
          <Button type="submit" loading={busy}>
            {email ? t.invite : t.copyLink}
          </Button>
        </form>
        {msg ? (
          <div className="mb-4">
            <Alert kind="success">{msg}</Alert>
          </div>
        ) : null}

        <h3 className="mb-2 text-sm font-semibold text-muted">{t.members}</h3>
        <ul className="mb-6 divide-y divide-border">
          {data?.members.length === 0 ? <li className="py-2 text-sm text-dim">—</li> : null}
          {data?.members.map((m) => (
            <li key={m.id} className="flex items-center gap-3 py-2 text-sm">
              <span className="min-w-0 flex-1 truncate">
                <span className="font-semibold">{m.name}</span> <span className="text-dim">{m.email}</span>
              </span>
              <select
                value={m.role}
                onChange={(e) => setMemberRole(m.id, e.target.value as GuestRole)}
                className="rounded-sm border border-border bg-panel-2 px-2 py-1"
              >
                {GUEST_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {t.roles[r]}
                  </option>
                ))}
              </select>
              <button type="button" onClick={() => removeMember(m.id)} className="text-dim hover:text-error">
                {t.remove}
              </button>
            </li>
          ))}
        </ul>

        <h3 className="mb-2 text-sm font-semibold text-muted">{t.pending}</h3>
        <ul className="divide-y divide-border">
          {data?.invites.filter((i) => !i.revokedAt && (i.maxUses == null || i.useCount < i.maxUses))
            .length === 0 ? (
            <li className="py-2 text-sm text-dim">—</li>
          ) : null}
          {data?.invites
            .filter((i) => !i.revokedAt && (i.maxUses == null || i.useCount < i.maxUses))
            .map((i) => (
              <li key={i.id} className="flex items-center gap-3 py-2 text-sm">
                <span className="min-w-0 flex-1 truncate">{i.email ?? t.shareLink} </span>
                <span className="text-dim">{t.roles[i.role]}</span>
                <button type="button" onClick={() => revoke(i.id)} className="text-dim hover:text-error">
                  {t.remove}
                </button>
              </li>
            ))}
        </ul>
        <PublishSection boardId={boardId} />
        <div className="mt-6 text-right">
          <Button variant="ghost" onClick={onClose}>
            {t.cancel}
          </Button>
        </div>
      </div>
    </div>
  );
}
